/**
 * Infinite Illusion — ii-theme.js
 * Scroll reveals, the light/dark theme toggle button, robust gradient
 * text-fill, and a supplementary mega-menu close-delay.
 * The pre-first-paint theme read/apply lives inline in snippets/ii-head.liquid,
 * not here — this file only handles the click handler and DOM injection.
 *
 * The CartLinesUpdateEvent import is used only by the sample-booklets add
 * to cart flow (initSamplesCart) — dispatching the same event the native
 * buy-buttons block dispatches is what makes cart-items-component and
 * cart-drawer-component refresh correctly, without reimplementing their
 * section-morphing logic here. Import must stay at module top level (ES
 * modules only allow static imports there), so it sits above the IIFE
 * that wraps the rest of this file; the IIFE's closures still see it fine.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function markNativeSectionsForReveal() {
    // Custom sections (e.g. ii-trust-strip) hand-place .ii-reveal on their
    // own cards for a finer per-card stagger; this catches native Pitch
    // sections, which we can't add the class to at the template level
    // without editing core section files. Sections already visible on load
    // are left alone so above-the-fold content never gets hidden.
    var candidates = document.querySelectorAll('#MainContent > .shopify-section');
    candidates.forEach(function (section) {
      if (section.querySelector('.ii-reveal')) return; // already opts in per-card
      if (isInViewport(section)) return;
      section.classList.add('ii-reveal');
    });
  }

  // Single observer shared across the initial pass and any content injected
  // later (e.g. the AJAX filter-tabs swap) — new .ii-reveal elements must be
  // handed to this same instance via observeReveal(), since an element only
  // fades in once something actually calls io.observe() on it.
  var revealObserver = null;

  function getRevealObserver() {
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry, index) {
            if (!entry.isIntersecting) return;
            var delay = index * 60;
            entry.target.style.transitionDelay = delay + 'ms';
            entry.target.classList.add('ii-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12 }
      );
    }
    return revealObserver;
  }

  function observeReveal(elements) {
    if (!elements.length) return;
    if (prefersReducedMotion) {
      elements.forEach(function (el) {
        el.classList.add('ii-in');
      });
      return;
    }
    var io = getRevealObserver();
    elements.forEach(function (el) {
      io.observe(el);
    });
  }

  function initScrollReveal() {
    markNativeSectionsForReveal();
    observeReveal(Array.prototype.slice.call(document.querySelectorAll('.ii-reveal')));
  }

  var themeToggleButtons = [];

  function updateThemeToggleLabels() {
    var current = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
    var next = current === 'light' ? 'dark' : 'light';
    themeToggleButtons.forEach(function (button) {
      button.setAttribute('aria-label', 'Switch to ' + next + ' mode (currently ' + current + ' mode)');
    });
  }

  function applyTheme(theme) {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('ii-theme', theme);
    } catch (e) {
      /* localStorage unavailable (private mode / disabled) — theme still applies for this load */
    }
    updateThemeToggleLabels();
  }

  // Two-state "Light / Dark" text switch, not an icon — active/inactive
  // emphasis is pure CSS (html[data-theme] descendant selectors in
  // ii-theme.css), so it's correct on first paint before this script runs,
  // matching the pre-paint script in ii-head.liquid. aria-label is the only
  // thing JS needs to keep current.
  function createThemeToggle() {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'ii-theme-toggle';

    var light = document.createElement('span');
    light.className = 'ii-theme-toggle__option ii-theme-toggle__option--light';
    light.setAttribute('aria-hidden', 'true');
    light.textContent = 'Light';

    var divider = document.createElement('span');
    divider.className = 'ii-theme-toggle__divider';
    divider.setAttribute('aria-hidden', 'true');
    divider.textContent = '/';

    var dark = document.createElement('span');
    dark.className = 'ii-theme-toggle__option ii-theme-toggle__option--dark';
    dark.setAttribute('aria-hidden', 'true');
    dark.textContent = 'Dark';

    button.appendChild(light);
    button.appendChild(divider);
    button.appendChild(dark);

    button.addEventListener('click', function () {
      var current = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
      applyTheme(current === 'light' ? 'dark' : 'light');
    });

    themeToggleButtons.push(button);
    return button;
  }

  // Small icon-only twin of createThemeToggle() for the mobile drawer (see
  // injectMobileThemeToggleEmblem below) — a single "◐" glyph rather than
  // the "Light / Dark" text pair, since the drawer's utility row is a
  // tighter, icon-driven context (sits next to the country/language
  // selector). Still theme-aware via aria-label only; the glyph itself
  // doesn't need to change per state, it represents the toggle action.
  function createThemeToggleEmblem() {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'ii-theme-toggle-emblem';
    var glyph = document.createElement('span');
    glyph.className = 'ii-theme-toggle-emblem__glyph';
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = '◑';
    button.appendChild(glyph);
    button.addEventListener('click', function () {
      var current = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
      applyTheme(current === 'light' ? 'dark' : 'light');
    });
    themeToggleButtons.push(button);
    return button;
  }

  // Lives in .header__column--left — the same native flex container the
  // mobile drawer hamburger (.header__drawer) occupies — rather than at a
  // hardcoded absolute offset. That column is already display:flex with a
  // 20px native gap, so inserting a sibling right after the hamburger
  // lays the toggle out correctly on desktop (hamburger is display:none,
  // toggle just reads as the leftmost item), left of the centered logo.
  //
  // NOT used on mobile: .header__column--left is display:contents at that
  // breakpoint, so its children stop being a flex group and instead
  // become direct auto-placed items of the header's OWN 5-column grid
  // (44/44/322/44/44 — hamburger, search, logo, account, cart). Adding a
  // 3rd auto-placed item on the left shifts every item after it by one
  // grid track, which is what was bumping the logo out of its own
  // 322px-wide track and under the toggle text. ii-theme.css hides this
  // instance under max-width:749px for exactly that reason — the mobile
  // equivalent is the icon injected into the drawer instead, see
  // injectMobileThemeToggleEmblem.
  function injectThemeToggle() {
    document.querySelectorAll('.header__column--left').forEach(function (col) {
      if (col.querySelector('.ii-theme-toggle')) return;
      var toggle = createThemeToggle();
      var drawer = col.querySelector('.header__drawer');
      if (drawer) {
        drawer.insertAdjacentElement('afterend', toggle);
      } else {
        col.insertBefore(toggle, col.firstChild);
      }
    });
    updateThemeToggleLabels();
  }

  // Mobile-only icon toggle, placed in the drawer's own utility row
  // (alongside the country/language selector) — plain block-flow content,
  // not a display:contents grid, so it can't collide with anything the
  // way the header instance did.
  function injectMobileThemeToggleEmblem() {
    document.querySelectorAll('.menu-drawer__utility-links').forEach(function (row) {
      if (row.querySelector('.ii-theme-toggle-emblem')) return;
      row.appendChild(createThemeToggleEmblem());
    });
    updateThemeToggleLabels();
  }

  // The native header-logo block (_header-logo) is disabled in
  // header-group.json — Shopify's image_picker "Logo" setting only accepts
  // images uploaded to the shop's own file library (shopify://shop_images/...),
  // not a theme asset, so assets/ii-infinity-logo.png can't be wired in as
  // the native logo from code. This injects the lockup centered in the
  // header's top row (menu_row is "bottom" in header-group.json, so the
  // nav has its own row underneath — see menu_position: "center" there).
  // Structure matches the brand wordmark: "INFINITE" — icon — "ILLUSION"
  // in one line, with "EXOTIC VINYL WRAPS" centered underneath the full
  // line. Same markup at every width — ii-theme.css scales it down on
  // mobile rather than hiding the wordmark down to icon-only.
  //
  // Centered via ii-theme.css absolute-positioning .ii-logo-link against
  // .header__columns, not the CSS grid's "center" track — that track sizes
  // to content and lands wherever the widest column ends up, not the true
  // visual midpoint of the row.
  function injectBrandLogo() {
    var iconUrl = window.iiAssetUrls && window.iiAssetUrls.infinityLogo;
    if (!iconUrl) return;

    // Scoped to .header__row--top: the bottom row (nav) has its own
    // .header__columns, and an unscoped query would duplicate the lockup
    // into that row too.
    document.querySelectorAll('.header__row--top .header__columns').forEach(function (columns) {
      if (columns.querySelector('.ii-logo-link')) return;

      var link = document.createElement('a');
      link.href = '/';
      link.className = 'ii-logo-link';
      link.setAttribute('aria-label', 'Infinite Illusion — home');

      var row = document.createElement('span');
      row.className = 'ii-logo-row';

      var wordInfinite = document.createElement('span');
      wordInfinite.className = 'ii-logo-word';
      wordInfinite.textContent = 'Infinite';

      // A masked span, not an <img>: ii-infinity-logo.png already has its
      // own baked-in static rainbow (real pixel colors, not a flat shape +
      // CSS gradient), so there's no way to animate ITS colors directly.
      // Using the PNG as a mask-image instead — browsers mask raster
      // images by alpha channel, which this asset has cleanly (transparent
      // outside the symbol, opaque within, confirmed via PIL) — discards
      // its baked-in color and uses only its shape, then the flowing
      // --ii-grad-loop background (same token/keyframes as the nav
      // hairline etc.) shows through and animates. aria-hidden since the
      // parent .ii-logo-link already carries the accessible name.
      var img = document.createElement('span');
      img.setAttribute('aria-hidden', 'true');
      img.className = 'ii-logo-icon';
      img.style.webkitMaskImage = 'url(' + iconUrl + ')';
      img.style.maskImage = 'url(' + iconUrl + ')';

      var wordIllusion = document.createElement('span');
      wordIllusion.className = 'ii-logo-word';
      wordIllusion.textContent = 'Illusion';

      row.appendChild(wordInfinite);
      row.appendChild(img);
      row.appendChild(wordIllusion);
      link.appendChild(row);

      var tagline = document.createElement('span');
      tagline.className = 'ii-logo-tagline';
      tagline.textContent = 'Exotic Vinyl Wraps';
      link.appendChild(tagline);

      columns.appendChild(link);
    });
  }

  // Shopify's inline_richtext fields (the announcement bar's text setting)
  // only round-trip bold/italic through the editor UI — any hand-typed
  // <span class="ii-grad-text"> gets silently stripped the next time the
  // field is saved in the theme editor. So instead of depending on that
  // span surviving, mark the keyword with *italic* or **bold** (real
  // formatting the editor preserves) and apply the class here, fresh on
  // every load, plus re-apply whenever the header re-renders underneath
  // us (announcement slider, section hydration, richtext re-save).
  var GRADIENT_TEXT_HOST_SELECTOR = '.announcement-bar__text, [data-ii-grad-scope]';
  var GRADIENT_TEXT_MARK_SELECTOR = 'em, i, strong, b';

  function applyGradientTextClass() {
    document.querySelectorAll(GRADIENT_TEXT_HOST_SELECTOR).forEach(function (host) {
      host.querySelectorAll(GRADIENT_TEXT_MARK_SELECTOR).forEach(function (mark) {
        mark.classList.add('ii-grad-text');
      });
    });
  }

  // Pitch's own mega menu already bridges the trigger-to-panel gap with a
  // pointer "safety box" (header-menu.js #onPointerMove / data-safety-box)
  // sized to match the real layout — see the comment on
  // .menu-list__link-title in ii-theme.css for how an earlier version of
  // this file's CSS accidentally broke that bridge. This is a
  // supplementary net only: if the pointer somehow still ends up outside
  // both the trigger and the panel when it closes (fast flicks, safety box
  // not yet sized on first hover), reopen it if the pointer is still
  // roughly in the nav area ~200ms later. Reuses the same "dispatch a real
  // pointerenter on the list item" trick header-menu.js itself uses
  // internally (#reconcilePointerTarget), so it exercises the component's
  // own public activate() rather than reimplementing it.
  var MEGA_MENU_GRACE_MS = 200;
  var lastPointer = { x: -1, y: -1 };
  var megaMenuPointerTrackingBound = false;

  function bindMegaMenuGraceListeners() {
    document.querySelectorAll('header-menu .menu-list__list-item').forEach(function (item) {
      if (item.dataset.iiGraceBound === 'true') return;

      var submenu = item.querySelector(':scope > [ref="submenu[]"]');
      if (!submenu) return;

      item.dataset.iiGraceBound = 'true';
      item.addEventListener('pointerleave', function () {
        setTimeout(function () {
          if (item.matches(':hover') || submenu.matches(':hover')) return;

          var linkRect = item.getBoundingClientRect();
          var subRect = submenu.getBoundingClientRect();
          var stillNearby =
            lastPointer.x >= Math.min(linkRect.left, subRect.left) &&
            lastPointer.x <= Math.max(linkRect.right, subRect.right) &&
            lastPointer.y >= linkRect.top &&
            lastPointer.y <= subRect.bottom;

          if (stillNearby) {
            item.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
          }
        }, MEGA_MENU_GRACE_MS);
      });
    });
  }

  function initMegaMenuGrace() {
    if (!megaMenuPointerTrackingBound) {
      megaMenuPointerTrackingBound = true;
      document.addEventListener(
        'pointermove',
        function (event) {
          lastPointer.x = event.clientX;
          lastPointer.y = event.clientY;
        },
        { passive: true }
      );
    }
    bindMegaMenuGraceListeners();
  }

  // Company name + tier badge for a signed-in B2B buyer, sourced from
  // window.iiB2B (emitted server-side by snippets/ii-head.liquid from the
  // real customer.b2b?/customer.current_company Liquid objects — this
  // function only reads and renders that data, it never determines B2B
  // status itself). Inserted as a plain sibling in .header__column--right,
  // right before <header-actions>, the same "sibling insertion" approach
  // injectThemeToggle/injectBrandLogo use elsewhere in this file, rather
  // than reaching inside header-actions' own light DOM — keeps this
  // decoupled from that native custom element's internal rendering.
  function injectB2BBadge() {
    if (!window.iiB2B || !window.iiB2B.isB2B) return;

    document.querySelectorAll('.header__column--right').forEach(function (col) {
      if (col.querySelector('.ii-b2b-badge')) return;

      var badge = document.createElement('div');
      badge.className = 'ii-b2b-badge';

      if (window.iiB2B.companyName) {
        var company = document.createElement('span');
        company.className = 'ii-b2b-badge__company';
        company.textContent = window.iiB2B.companyName;
        badge.appendChild(company);
      }

      if (window.iiB2B.tier) {
        var tier = document.createElement('span');
        tier.className = 'ii-b2b-badge__tier';
        tier.textContent = window.iiB2B.tier;
        badge.appendChild(tier);
      }

      if (!badge.childNodes.length) return;

      var actions = col.querySelector('header-actions');
      if (actions) {
        col.insertBefore(badge, actions);
      } else {
        col.appendChild(badge);
      }
    });
  }

  function initHeaderEnhancements() {
    injectThemeToggle();
    injectMobileThemeToggleEmblem();
    injectBrandLogo();
    injectB2BBadge();
    applyGradientTextClass();
    initMegaMenuGrace();

    // Horizon re-renders header markup on section changes in the theme
    // editor (and on section-rendering-API updates); watch for it so
    // everything above — none of which is part of any section's own
    // Liquid output or listener wiring — survives those re-renders
    // instead of only working once on load.
    var headerGroup = document.getElementById('header-group');
    if (!headerGroup || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function () {
      injectThemeToggle();
      injectMobileThemeToggleEmblem();
      injectBrandLogo();
      injectB2BBadge();
      applyGradientTextClass();
      bindMegaMenuGraceListeners();
    });
    observer.observe(headerGroup, { childList: true, subtree: true });
  }

  // Contact page router (sections/ii-contact-router.liquid +
  // sections/ii-contact-form.liquid): clicking a "selectable" card sets
  // the contact form's subject <select> to that card's value and marks
  // the card active. The two sections only share a data attribute
  // (data-contact-subject on the select), not element IDs, so either can
  // be moved, removed, or reused independently without breaking the other.
  function initContactRouter() {
    var cards = document.querySelectorAll('.ii-contact-router__card--selectable');
    if (!cards.length) return;

    var select = document.querySelector('[data-contact-subject]');

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (c) {
          c.classList.remove('is-active');
        });
        card.classList.add('is-active');

        if (select) {
          select.value = card.dataset.contactRouterValue;
        }
      });
    });
  }

  // Film finder (sections/ii-film-finder.liquid): single-select chips per
  // question row; once both rows have a selection, looks up the matching
  // recommendation (rendered inline as a JSON payload by the section, built
  // from editable "recommendation" blocks) and reveals it. Matching is by
  // lowercased option text, not a hidden ID, so a merchant can rename
  // options and matching recommendation blocks together with no code.
  function initFilmFinder() {
    document.querySelectorAll('[data-ii-film-finder]').forEach(function (root) {
      if (root.dataset.iiFinderBound === 'true') return;
      root.dataset.iiFinderBound = 'true';

      var mapEl = root.querySelector('[data-ii-finder-map]');
      var map = [];
      try {
        map = JSON.parse(mapEl.textContent);
      } catch (e) {
        map = [];
      }

      var selections = {};
      var resultEl = root.querySelector('[data-ii-finder-result]');
      var resultTextEl = root.querySelector('[data-ii-finder-result-text]');
      var resultLinkEl = root.querySelector('[data-ii-finder-result-link]');

      function updateResult() {
        if (Object.keys(selections).length < 2) {
          resultEl.hidden = true;
          return;
        }
        var match = map.filter(function (entry) {
          return entry.want === selections['0'] && entry.vehicle === selections['1'];
        })[0];
        if (!match) {
          resultEl.hidden = true;
          return;
        }
        resultTextEl.textContent = match.text;
        if (match.link) {
          resultLinkEl.href = match.link;
          resultLinkEl.hidden = false;
        } else {
          resultLinkEl.hidden = true;
        }
        resultEl.hidden = false;
      }

      root.querySelectorAll('[data-ii-finder-question]').forEach(function (questionEl) {
        var index = questionEl.dataset.iiFinderQuestion;
        questionEl.querySelectorAll('[data-ii-finder-option]').forEach(function (chip) {
          chip.addEventListener('click', function () {
            questionEl.querySelectorAll('[data-ii-finder-option]').forEach(function (c) {
              c.classList.remove('is-active');
            });
            chip.classList.add('is-active');
            selections[index] = chip.dataset.iiFinderOption;
            updateResult();
          });
        });
      });
    });
  }

  // Compare Finishes page — draggable before/after slider (see
  // sections/ii-ab-slider.liquid). One handler per .ii-ab-slider__frame;
  // Pointer Events cover mouse + touch drag in a single code path.
  // ArrowLeft / ArrowRight move the handle 5% per press when a frame has
  // focus (role="slider"). Multiple "pair" blocks render server-side and
  // are toggled by hiding/showing frames on switch-button click, so no
  // image data has to move through JS.
  function initAbSlider() {
    document.querySelectorAll('[data-ii-ab-slider]').forEach(function (root) {
      if (root.dataset.iiAbBound === 'true') return;
      root.dataset.iiAbBound = 'true';

      var frames = root.querySelectorAll('[data-ii-ab-frame]');

      function setPosition(frame, pct) {
        pct = Math.max(0, Math.min(100, pct));
        var b = frame.querySelector('[data-ii-ab-b]');
        var handle = frame.querySelector('[data-ii-ab-handle]');
        var knob = frame.querySelector('[data-ii-ab-knob]');
        b.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
        handle.style.left = pct + '%';
        knob.style.left = pct + '%';
        frame.setAttribute('aria-valuenow', Math.round(pct));
      }

      function pctFromEvent(frame, e) {
        var rect = frame.getBoundingClientRect();
        var clientX = e.touches ? e.touches[0].clientX : e.clientX;
        return ((clientX - rect.left) / rect.width) * 100;
      }

      frames.forEach(function (frame) {
        var dragging = false;

        frame.addEventListener('pointerdown', function (e) {
          dragging = true;
          if (frame.setPointerCapture) frame.setPointerCapture(e.pointerId);
          setPosition(frame, pctFromEvent(frame, e));
        });
        frame.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          setPosition(frame, pctFromEvent(frame, e));
        });
        frame.addEventListener('pointerup', function () {
          dragging = false;
        });
        frame.addEventListener('pointercancel', function () {
          dragging = false;
        });

        frame.addEventListener('keydown', function (e) {
          var current = parseFloat(frame.getAttribute('aria-valuenow')) || 50;
          if (e.key === 'ArrowLeft') {
            setPosition(frame, current - 5);
            e.preventDefault();
          } else if (e.key === 'ArrowRight') {
            setPosition(frame, current + 5);
            e.preventDefault();
          }
        });
      });

      root.querySelectorAll('[data-ii-ab-switch]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.dataset.iiAbTarget;
          root.querySelectorAll('[data-ii-ab-switch]').forEach(function (b) {
            b.classList.remove('is-active');
          });
          btn.classList.add('is-active');
          frames.forEach(function (frame) {
            frame.hidden = frame.dataset.iiAbIndex !== target;
          });
        });
      });
    });
  }

  // Compare Finishes page — decision helper (see
  // sections/ii-finish-helper.liquid). Same selection-then-lookup pattern
  // as initFilmFinder above, kept as a separate function (different data
  // attributes, richer result payload: title + explanation + button, not
  // just a text line + link) rather than generalizing the two, so each
  // section's markup can evolve independently.
  function initFinishHelper() {
    document.querySelectorAll('[data-ii-helper]').forEach(function (root) {
      if (root.dataset.iiHelperBound === 'true') return;
      root.dataset.iiHelperBound = 'true';

      var mapEl = root.querySelector('[data-ii-helper-map]');
      var map = [];
      try {
        map = JSON.parse(mapEl.textContent);
      } catch (e) {
        map = [];
      }

      var selections = {};
      var resultEl = root.querySelector('[data-ii-helper-result]');
      var titleEl = root.querySelector('[data-ii-helper-title]');
      var explanationEl = root.querySelector('[data-ii-helper-explanation]');
      var buttonEl = root.querySelector('[data-ii-helper-button]');

      function updateResult() {
        if (Object.keys(selections).length < 2) {
          resultEl.hidden = true;
          return;
        }
        var match = map.filter(function (entry) {
          return entry.option1 === selections['0'] && entry.option2 === selections['1'];
        })[0];
        if (!match) {
          resultEl.hidden = true;
          return;
        }
        titleEl.textContent = match.title;
        explanationEl.textContent = match.explanation;
        if (match.link) {
          buttonEl.href = match.link;
          buttonEl.textContent = match.buttonLabel || 'Shop now';
          buttonEl.hidden = false;
        } else {
          buttonEl.hidden = true;
        }
        resultEl.hidden = false;
      }

      root.querySelectorAll('[data-ii-helper-question]').forEach(function (questionEl) {
        var index = questionEl.dataset.iiHelperQuestion;
        questionEl.querySelectorAll('[data-ii-helper-option]').forEach(function (chip) {
          chip.addEventListener('click', function () {
            questionEl.querySelectorAll('[data-ii-helper-option]').forEach(function (c) {
              c.classList.remove('is-active');
            });
            chip.classList.add('is-active');
            selections[index] = chip.dataset.iiHelperOption;
            updateResult();
          });
        });
      });
    });
  }

  // Order-samples modal (see snippets/ii-samples-modal.liquid). Built on a
  // native <dialog> — showModal() gives native focus trapping and
  // Escape-to-close for free, so there's no hand-rolled focus-trap loop
  // here. Opened by any [data-samples-modal] element, and also by any
  // link whose href resolves to /pages/samples, since Pitch's native
  // `button` block schema has no way to attach a custom data attribute —
  // every existing "Order samples" button already placed via that block
  // type across the site links to /pages/samples, so href-matching is how
  // they get intercepted instead of navigating.
  function initSamplesModal() {
    var dialog = document.querySelector('[data-ii-samples-dialog]');
    if (!dialog) return;

    var lastFocus = null;

    function openModal(trigger) {
      lastFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        document.body.style.overflow = '';
        if (lastFocus) lastFocus.focus();
      }
    }

    dialog.addEventListener('close', function () {
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    });

    // Native <dialog> backdrop clicks report the dialog itself as the
    // click target (nothing else in the box model sits "outside" the
    // panel but "inside" the dialog), so this is the standard way to
    // detect a backdrop click without a separate scrim element.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeModal();
    });

    var closeButton = dialog.querySelector('[data-ii-samples-close]');
    if (closeButton) closeButton.addEventListener('click', closeModal);

    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var trigger = event.target.closest(
        '[data-samples-modal], a[href="/pages/samples"], a[href^="/pages/samples?"], a[href^="/pages/samples#"]'
      );
      if (!trigger) return;
      // The modal's own "See full details" link also points at
      // /pages/samples — without this check it would match its own
      // trigger selector and just reopen the already-open dialog instead
      // of letting the click navigate through to the full page.
      if (trigger.closest('[data-ii-samples-dialog]')) return;
      event.preventDefault();
      openModal(trigger);
    });

    dialog.closeSamplesModal = closeModal;
  }

  // Shared add-to-cart for the sample-booklets system (booklet cards,
  // bundle strip, and PPF singles quick-add on the full page, plus the
  // modal's own booklet/bundle buttons) — see [data-ii-add-to-cart]
  // buttons in sections/ii-booklet-cards.liquid, ii-bundle-strip.liquid,
  // ii-samples-singles-grid.liquid, and snippets/ii-samples-modal.liquid.
  // Dispatches the same CartLinesUpdateEvent the native buy-buttons block
  // dispatches (see blocks/buy-buttons.liquid / assets/product-form.js)
  // so cart-items-component and cart-drawer-component refresh the drawer
  // content exactly as they would for a normal product-page add — this
  // file never touches that DOM directly.
  function addToCartRequest(variantId) {
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (item) {
      if (item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    var formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('sections', sectionIds.join(','));

    var deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'ii-samples',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    return fetch(window.Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'text/html' },
      body: formData,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        var didError = Boolean(response.status);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
          detail: {
            didError: didError,
            items: response.items,
            source: 'ii-samples',
            sourceId: String(variantId),
            itemCount: 1,
          },
        });
        if (didError) throw new Error(response.message || 'Add to cart failed');
        return response;
      })
      .catch(function (error) {
        deferred.reject(error);
        throw error;
      });
  }

  // Multi-item add for the account dashboard's Reorder button (see
  // snippets/ii-order-card.liquid) — re-adds every line item from a past
  // order in one request. Not a loop of addToCartRequest above: looping
  // would mean N network round-trips and N CartLinesUpdateEvent dispatches
  // for a single click (the cart drawer would visibly re-render N times).
  // Same event contract as addToCartRequest (one event, N `lines` entries
  // instead of one), but POSTs to cart_add_url as a JSON body
  // ({ items: [...] }) since the form-encoded single-id/quantity POST
  // addToCartRequest uses has no multi-item form.
  function addMultipleToCartRequest(items) {
    // items: [{ id: variantId, quantity: N }, ...]
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (item) {
      if (item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    var lines = items.map(function (item) {
      return { merchandiseId: item.id, quantity: item.quantity };
    });

    var deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'ii-account-reorder',
        lines: lines,
        promise: deferred.promise,
      })
    );

    return fetch(window.Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionIds.join(',') }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        var didError = Boolean(response.status);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
          detail: {
            didError: didError,
            items: response.items,
            source: 'ii-account-reorder',
            sourceId: items
              .map(function (i) {
                return i.id;
              })
              .join(','),
            itemCount: items.length,
          },
        });
        if (didError) throw new Error(response.message || 'Reorder failed');
        return response;
      })
      .catch(function (error) {
        deferred.reject(error);
        throw error;
      });
  }

  // Wires up [data-ii-reorder] buttons on the account dashboard's order
  // cards (snippets/ii-order-card.liquid). Each button carries
  // data-order-items='[{"id":123,"quantity":2},...]' — one JSON attribute
  // rather than N separate data-* attributes, since order line-item counts
  // are unbounded and Liquid can serialize the array directly.
  function initAccountReorder() {
    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var button = event.target.closest('[data-ii-reorder]');
      if (!button || button.disabled) return;

      var raw = button.dataset.orderItems;
      if (!raw) return;

      var items;
      try {
        items = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!items.length) return;

      var originalText = button.textContent;
      button.disabled = true;

      addMultipleToCartRequest(items)
        .then(function () {
          button.textContent = window.Theme.translations.added || 'Added';

          var cartDrawer = document.querySelector('#cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          setTimeout(function () {
            button.textContent = originalText;
            button.disabled = false;
          }, 1500);
        })
        .catch(function () {
          button.textContent = originalText;
          button.disabled = false;
        });
    });
  }

  function initSamplesCart() {
    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var button = event.target.closest('[data-ii-add-to-cart]');
      if (!button || button.disabled) return;

      var variantId = button.dataset.variantId;
      if (!variantId) return;

      var originalText = button.textContent;
      button.disabled = true;

      addToCartRequest(variantId)
        .then(function () {
          button.textContent = window.Theme.translations.added || 'Added';

          var fromModal = button.closest('[data-ii-samples-dialog]');
          if (fromModal && typeof fromModal.closeSamplesModal === 'function') {
            fromModal.closeSamplesModal();
          }

          var cartDrawer = document.querySelector('#cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          setTimeout(function () {
            button.textContent = originalText;
            button.disabled = false;
          }, 1500);
        })
        .catch(function () {
          button.textContent = originalText;
          button.disabled = false;
        });
    });
  }

  // Ultimate Exotic Series notify bar (sections/ii-notify-bar.liquid) —
  // countdown to an editable target datetime. Ships server-side as
  // `hidden` so no-JS/slow-JS visitors never see an unpopulated "00"
  // countdown; this reveals it once real numbers are computed, and hides
  // it again for good once the target has passed ("hides itself once
  // passed" per the brief). Re-checks every 30s rather than every
  // second, since the display only ever shows whole minutes.
  function initCountdown() {
    document.querySelectorAll('[data-ii-countdown]').forEach(function (el) {
      var target = new Date(el.dataset.target).getTime();
      if (isNaN(target)) return;

      var daysEl = el.querySelector('[data-ii-cd-days]');
      var hoursEl = el.querySelector('[data-ii-cd-hours]');
      var minsEl = el.querySelector('[data-ii-cd-mins]');
      var timer;

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        var diff = target - Date.now();
        if (diff <= 0) {
          el.hidden = true;
          if (timer) clearInterval(timer);
          return;
        }
        el.hidden = false;
        var totalMinutes = Math.floor(diff / 60000);
        daysEl.textContent = pad(Math.floor(totalMinutes / (60 * 24)));
        hoursEl.textContent = pad(Math.floor((totalMinutes % (60 * 24)) / 60));
        minsEl.textContent = pad(totalMinutes % 60);
      }

      tick();
      timer = setInterval(tick, 30000);
    });
  }

  // Favoriting ("save" hearts on snippets/resource-card.liquid, feeding
  // the account dashboard's Saved colors grid). This store has no app or
  // Storefront-API integration, and Liquid itself has no write access to
  // customer data — a real customer metafield isn't reachable from theme
  // code alone. localStorage, keyed by window.iiCustomerId (set in
  // snippets/ii-head.liquid), is the mechanism that's actually available:
  // it survives reloads and keeps multiple accounts on a shared device
  // separate, but it's device-local, not synced server-side or across
  // devices. Entries store the resource's HANDLE (not numeric id) because
  // initSavedColors re-fetches full product data from the public
  // /products/{handle}.js endpoint, which takes a handle.
  function getFavoritesStorageKey() {
    return 'ii-favorites-' + (window.iiCustomerId || 'guest');
  }

  function readFavorites() {
    try {
      var raw = localStorage.getItem(getFavoritesStorageKey());
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function writeFavorites(list) {
    try {
      localStorage.setItem(getFavoritesStorageKey(), JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable (private mode / disabled) — favoriting
         silently no-ops for this session rather than throwing. */
    }
  }

  function isFavorited(list, type, id) {
    return list.some(function (entry) {
      return entry.type === type && entry.id === id;
    });
  }

  function updateFavoriteButtons(type, id, active) {
    document
      .querySelectorAll('[data-ii-favorite][data-favorite-type="' + type + '"]')
      .forEach(function (btn) {
        if (btn.dataset.favoriteId !== id) return;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
  }

  function initFavorites() {
    var list = readFavorites();
    document.querySelectorAll('[data-ii-favorite]').forEach(function (btn) {
      if (isFavorited(list, btn.dataset.favoriteType, btn.dataset.favoriteId)) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      }
    });

    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var btn = event.target.closest('[data-ii-favorite]');
      if (!btn) return;
      event.preventDefault();

      var type = btn.dataset.favoriteType;
      var id = btn.dataset.favoriteId;
      if (!id) return;

      var current = readFavorites();
      var active = isFavorited(current, type, id);
      if (active) {
        current = current.filter(function (entry) {
          return !(entry.type === type && entry.id === id);
        });
      } else {
        current.push({ type: type, id: id });
      }
      writeFavorites(current);
      updateFavoriteButtons(type, id, !active);
      document.dispatchEvent(new CustomEvent('ii:favorites-changed'));
    });
  }

  // Renders the account dashboard's "Saved colors" grid (sections/
  // ii-account-dashboard.liquid) from the favorites list above — this has
  // to happen client-side since Liquid can't read localStorage. Only the
  // handful of fields the /products/{handle}.js AJAX endpoint actually
  // returns are available (no metafields, unlike snippets/ii-swatch-chip.
  // liquid's server-side fallback chain), so the color swatch here checks
  // a color-* tag then falls back to the product photo — metafield-based
  // swatches aren't reachable from this endpoint.
  var SWATCH_COLOR_FAMILIES = [
    ['red', '#c9262c'],
    ['orange', '#e0672c'],
    ['yellow', '#e8c227'],
    ['green', '#2f7d4f'],
    ['teal', '#1f8a8a'],
    ['blue', '#1e4d8f'],
    ['purple', '#5a2a7a'],
    ['pink', '#d1477a'],
    ['black', '#1b1b1b'],
    ['white', '#f2f2f2'],
    ['grey', '#8a8f96'],
    ['gold', '#c8942a'],
    ['bronze', '#8c5a2b'],
  ];

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function buildSavedColorCard(product) {
    var tags = product.tags || [];
    var color = null;
    for (var i = 0; i < SWATCH_COLOR_FAMILIES.length; i++) {
      if (tags.indexOf('color-' + SWATCH_COLOR_FAMILIES[i][0]) !== -1) {
        color = SWATCH_COLOR_FAMILIES[i][1];
        break;
      }
    }

    var url = '/products/' + product.handle;
    var card = document.createElement('div');
    card.className = 'ii-saved-color';

    var swatchLink = document.createElement('a');
    swatchLink.href = url;
    swatchLink.className = 'ii-saved-color__swatch-link';
    var swatch = document.createElement('span');
    swatch.className = 'ii-saved-color__swatch';
    if (color) {
      swatch.style.backgroundColor = color;
    } else if (product.featured_image) {
      swatch.style.backgroundImage = 'url(' + product.featured_image + ')';
    }
    swatchLink.appendChild(swatch);

    var info = document.createElement('div');
    info.className = 'ii-saved-color__info';

    var name = document.createElement('a');
    name.href = url;
    name.className = 'ii-saved-color__name';
    name.textContent = product.title;
    info.appendChild(name);

    if (product.type) {
      var finish = document.createElement('div');
      finish.className = 'ii-saved-color__finish';
      finish.textContent = product.type;
      info.appendChild(finish);
    }

    var row = document.createElement('div');
    row.className = 'ii-saved-color__row';

    var price = document.createElement('span');
    price.className = 'ii-saved-color__price';
    price.textContent = formatMoney(product.price);
    row.appendChild(price);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'ii-saved-color__remove';
    remove.textContent = 'Remove';
    remove.addEventListener('click', function () {
      var updated = readFavorites().filter(function (entry) {
        return !(entry.type === 'product' && entry.id === product.handle);
      });
      writeFavorites(updated);
      updateFavoriteButtons('product', product.handle, false);
      document.dispatchEvent(new CustomEvent('ii:favorites-changed'));
    });
    row.appendChild(remove);
    info.appendChild(row);

    card.appendChild(swatchLink);
    card.appendChild(info);
    return card;
  }

  function initSavedColors() {
    var container = document.querySelector('[data-ii-saved-colors]');
    if (!container) return;

    var grid = container.querySelector('[data-ii-saved-colors-grid]');
    var emptyState = container.querySelector('[data-ii-saved-colors-empty]');

    function render() {
      var favorites = readFavorites().filter(function (entry) {
        return entry.type === 'product';
      });

      grid.innerHTML = '';

      if (!favorites.length) {
        grid.hidden = true;
        if (emptyState) emptyState.hidden = false;
        return;
      }

      if (emptyState) emptyState.hidden = true;
      grid.hidden = false;

      favorites.forEach(function (entry) {
        fetch('/products/' + entry.id + '.js')
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (product) {
            if (!product) return;
            grid.appendChild(buildSavedColorCard(product));
          })
          .catch(function () {
            /* Product removed/unpublished since it was saved — skip it
               silently rather than showing a broken card. */
          });
      });
    }

    render();
    document.addEventListener('ii:favorites-changed', render);
  }

  // Account dashboard view-switcher (sections/ii-account-dashboard.liquid)
  // — turns the nav from pure in-page anchors into a real single-view
  // switcher: one section visible at a time, current view reflected in
  // the URL hash so it survives reload/sharing. Still one Liquid
  // template/one continuous DOM (see that section's own doc comment for
  // why — no theme-routable "addresses"/"order" template exists under
  // this store's hosted customer accounts), so this is a pure JS
  // enhancement: with JS disabled, every section stays visible and
  // scrollable exactly as before, nothing is lost.
  function initAccountNav() {
    var nav = document.querySelector('.ii-account-nav');
    if (!nav) return;

    var links = [].slice.call(nav.querySelectorAll('.ii-account-nav__link[href^="#"]'));
    if (!links.length) return;

    var sections = links.map(function (link) {
      return document.getElementById(link.getAttribute('href').slice(1));
    });

    function activate(id, updateHash) {
      var matchedIndex = -1;
      sections.forEach(function (section, index) {
        if (!section) return;
        if (section.id === id) matchedIndex = index;
      });
      if (matchedIndex === -1) matchedIndex = 0;

      sections.forEach(function (section, index) {
        if (!section) return;
        var isMatch = index === matchedIndex;
        section.hidden = !isMatch;
        links[index].classList.toggle('is-active', isMatch);
      });

      if (updateHash && window.history && history.replaceState) {
        history.replaceState(null, '', '#' + sections[matchedIndex].id);
      }
    }

    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        activate(link.getAttribute('href').slice(1), true);
      });
    });

    var initialId = window.location.hash ? window.location.hash.slice(1) : 'orders';
    activate(initialId, false);
  }

  // Email capture popup (snippets/ii-signup-popup.liquid). Same native
  // <dialog>/showModal() approach as initSamplesModal above (free focus
  // trap + Escape-to-close), plus: a delay-vs-scroll-depth trigger race,
  // a dismiss cookie that suppresses the auto-trigger for N days, a
  // minimized corner tab that survives a dismiss (and reappears
  // immediately, without waiting through the trigger again, on a later
  // visit still inside the suppress window), and permanent suppression
  // once the visitor has actually subscribed. localStorage — not the
  // cookie — is what makes "already subscribed" permanent and immune to
  // the N-day expiry, mirroring the ii-favorites-* localStorage
  // convention elsewhere in this file for the same underlying reason:
  // no app/Storefront-API integration exists to look up real subscriber
  // state client-side, so a local flag set at the moment of a real
  // successful submit is the only signal actually available.
  var SIGNUP_POPUP_COOKIE = 'ii_signup_popup_dismissed';
  var SIGNUP_POPUP_SUBSCRIBED_KEY = 'ii-signup-popup-subscribed';

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function initSignupPopup() {
    var dialog = document.querySelector('[data-ii-signup-popup-dialog]');
    var tab = document.querySelector('[data-ii-signup-popup-tab]');
    if (!dialog) return;

    var isSuccess = dialog.classList.contains('is-success');
    var alreadySubscribed = false;
    try {
      alreadySubscribed = window.localStorage.getItem(SIGNUP_POPUP_SUBSCRIBED_KEY) === 'true';
    } catch (e) {
      /* localStorage unavailable — treat as not-yet-subscribed rather than throwing */
    }

    // A real successful submit just happened (server round-trip) —
    // permanently suppress from here on, and show the result immediately
    // rather than making them wait through the normal trigger.
    if (isSuccess) {
      try {
        window.localStorage.setItem(SIGNUP_POPUP_SUBSCRIBED_KEY, 'true');
      } catch (e) {
        /* no-op */
      }
      alreadySubscribed = true;
    }

    function openDialog() {
      if (dialog.open) return;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
      if (tab) tab.hidden = true;
    }

    function closeDialog() {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        document.body.style.overflow = '';
      }
    }

    dialog.addEventListener('close', function () {
      document.body.style.overflow = '';

      // Converted — never show the popup or the tab again, no cookie needed.
      if (isSuccess || alreadySubscribed) return;

      var suppressDays = parseInt(dialog.dataset.suppressDays, 10);
      if (isNaN(suppressDays) || suppressDays < 0) suppressDays = 14;
      setCookie(SIGNUP_POPUP_COOKIE, '1', suppressDays);

      if (tab) tab.hidden = false;
    });

    // Native <dialog> backdrop clicks report the dialog itself as the
    // click target — see initSamplesModal's identical comment above.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog();
    });

    dialog.querySelectorAll('[data-ii-signup-popup-close]').forEach(function (button) {
      button.addEventListener('click', closeDialog);
    });

    if (tab) {
      tab.addEventListener('click', function () {
        tab.hidden = true;
        openDialog();
      });
    }

    // Tap-to-copy on the success-state code box.
    var codeButton = dialog.querySelector('[data-ii-signup-popup-copy-code]');
    if (codeButton) {
      var codeValueEl = dialog.querySelector('[data-ii-signup-popup-code-value]');
      var copylineEl = dialog.querySelector('[data-ii-signup-popup-copyline]');
      codeButton.addEventListener('click', function () {
        if (!codeValueEl || !navigator.clipboard || !navigator.clipboard.writeText) return;
        var code = codeValueEl.textContent.trim();
        navigator.clipboard
          .writeText(code)
          .then(function () {
            if (!copylineEl) return;
            var original = copylineEl.textContent;
            copylineEl.textContent = 'Copied!';
            copylineEl.dataset.copied = 'true';
            setTimeout(function () {
              copylineEl.textContent = original;
              delete copylineEl.dataset.copied;
            }, 1800);
          })
          .catch(function () {
            /* Clipboard permission denied — the code is still visible on screen, nothing else to do */
          });
      });
    }

    // A real submit just landed — open straight into the success state,
    // no trigger/cookie logic needed for this load. Must be checked before
    // the generic alreadySubscribed bail-out below: isSuccess is what SETS
    // alreadySubscribed = true a few lines up, so on this exact load
    // alreadySubscribed is already true and would otherwise skip this.
    if (isSuccess) {
      openDialog();
      return;
    }

    // Already converted (on an earlier visit): don't wire up any
    // trigger or tab-on-load logic at all.
    if (alreadySubscribed) return;

    var dismissedRecently = getCookie(SIGNUP_POPUP_COOKIE) !== null;
    if (dismissedRecently) {
      // Don't nag with the auto-trigger again inside the suppress window,
      // but don't delete the offer either — the corner tab stays reachable.
      if (tab) tab.hidden = false;
      return;
    }

    var delaySeconds = parseInt(dialog.dataset.delaySeconds, 10);
    if (isNaN(delaySeconds) || delaySeconds < 0) delaySeconds = 18;
    var scrollPercent = parseInt(dialog.dataset.scrollPercent, 10);
    if (isNaN(scrollPercent) || scrollPercent < 0) scrollPercent = 50;

    var triggered = false;
    var delayTimer = null;

    function trigger() {
      if (triggered) return;
      triggered = true;
      if (delayTimer) clearTimeout(delayTimer);
      window.removeEventListener('scroll', onScroll);
      openDialog();
    }

    function currentScrollPercent() {
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return 0;
      return (window.scrollY / docHeight) * 100;
    }

    var scrollTicking = false;
    function onScroll() {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(function () {
        scrollTicking = false;
        if (currentScrollPercent() >= scrollPercent) trigger();
      });
    }

    delayTimer = setTimeout(trigger, delaySeconds * 1000);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // AJAX mode for sections/ii-filter-tabs.liquid (section.settings.
  // ajax_navigation — see that section's own doc comment for why this is
  // a separate mode from the pre-existing in-page toggle). Delegated on
  // document rather than bound per-tab, since every click swaps this same
  // filter-tabs section's own HTML back in (to update the active-tab
  // state and descriptor text, server-computed from current_tags) —
  // direct listeners on the old elements would go stale the moment
  // they're replaced.
  function initAjaxFilterTabs() {
    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var tab = event.target.closest('.ii-filter-tabs[data-ajax-navigation="true"] .ii-filter-tab');
      if (!tab) return;

      var tabsEl = tab.closest('.ii-filter-tabs');
      var sectionIds = (tabsEl.dataset.ajaxSectionIds || '')
        .split(',')
        .map(function (id) {
          return id.trim();
        })
        .filter(Boolean);
      if (!sectionIds.length) return;

      event.preventDefault();

      var url = tab.getAttribute('href');
      var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'sections=' + sectionIds.join(',');

      fetch(fetchUrl)
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          sectionIds.forEach(function (id) {
            var html = data[id];
            if (typeof html !== 'string') return;
            var existing = document.getElementById('shopify-section-' + id);
            if (!existing) return;
            var temp = document.createElement('div');
            temp.innerHTML = html.trim();
            var incoming = temp.firstElementChild;
            if (!incoming) return;
            existing.replaceWith(incoming);
            var revealed = Array.prototype.slice.call(incoming.querySelectorAll('.ii-reveal'));
            if (incoming.classList.contains('ii-reveal')) revealed.push(incoming);
            observeReveal(revealed);
          });
        })
        .catch(function () {
          // Fetch/parse failed — fall back to a real navigation rather
          // than leaving the tab looking clicked with nothing changed.
          window.location.href = url;
        });
    });
  }

  function init() {
    initScrollReveal();
    initHeaderEnhancements();
    initContactRouter();
    initFilmFinder();
    initAbSlider();
    initFinishHelper();
    initSamplesModal();
    initSamplesCart();
    initAccountReorder();
    initCountdown();
    initFavorites();
    initSavedColors();
    initAccountNav();
    initSignupPopup();
    initAjaxFilterTabs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
